import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { CERT_LABEL, makeLabeler, makeRegionLabeler, useCommonCodes, useRegions } from '../lib/codes'

/**
 * 기업정보 상세 — 1기업 : N해결카드(설계서 M-01)를 화면에서 그대로 보여준다.
 * 연락처는 애초에 API가 내려주지 않는다(`org.ts`의 `PUBLIC_ORG_COLUMNS`).
 */
export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>()
  const org = useQuery({
    queryKey: ['org', id],
    queryFn: () => api.org(id ?? ''),
    enabled: Boolean(id),
  })

  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const categoryLabel = makeLabeler(useCommonCodes('category').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const o = org.data

  return (
    <>
      <Link className="back-link" to="/solutions">
        ← 기업해결카드 목록
      </Link>
      <QueryState isPending={org.isPending} error={org.error}>
        {o ? (
          <>
            <article className="panel">
              <div className="detail-head">
                <h1 className="page-title">
                  {o.name}{' '}
                  {o.certType && CERT_LABEL[o.certType] ? (
                    <span className={o.certType === 'cert_certified' ? 'badge cert' : 'badge pre'}>
                      {CERT_LABEL[o.certType]}
                    </span>
                  ) : null}
                </h1>
                <p className="muted" style={{ margin: 0 }}>
                  {orgTypeLabel(o.orgType)}
                </p>
              </div>

              {o.intro ? <p style={{ whiteSpace: 'pre-wrap' }}>{o.intro}</p> : null}

              {/*
                미션·비전은 신청 단계에서도 선택 항목이라 비는 게 정상이다.
                다른 항목처럼 "확인 필요"를 띄우면 빠뜨린 것처럼 읽혀서, 있을 때만 보여준다.
              */}
              {o.mission ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <b>미션</b> — {o.mission}
                </p>
              ) : null}
              {o.vision ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  <b>비전</b> — {o.vision}
                </p>
              ) : null}

              <dl className="kv">
                <dt>소재지</dt>
                <dd>
                  {o.baseRegionCode ? regionLabel(o.baseRegionCode) : <span className="muted">확인 필요</span>}
                </dd>

                <dt>설립</dt>
                <dd>{o.foundedYear !== null ? `${o.foundedYear}년` : <span className="muted">확인 필요</span>}</dd>

                <dt>구성원</dt>
                <dd>{o.memberScale ?? <span className="muted">확인 필요</span>}</dd>

                <dt>홈페이지</dt>
                <dd>
                  {o.homepageUrl ? (
                    <a href={o.homepageUrl} target="_blank" rel="noreferrer noopener">
                      {o.homepageUrl}
                    </a>
                  ) : (
                    <span className="muted">확인 필요</span>
                  )}
                </dd>
              </dl>
            </article>

            <h2 className="section-title">이 기업의 해결카드 {o.solutionCards.length}개</h2>
            {o.solutionCards.length === 0 ? (
              <p className="muted small">아직 공개된 해결카드가 없습니다.</p>
            ) : (
              <div className="grid">
                {o.solutionCards.map((c) => (
                  <Link key={c.id} className="card" to={`/solutions/${c.id}`}>
                    <h3>{c.title}</h3>
                    <p>{c.summary}</p>
                    <div className="tags">
                      {c.categoryCodes.map((code) => (
                        <span key={code} className="tag">
                          {categoryLabel(code)}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : null}
      </QueryState>
    </>
  )
}
